import React, { useEffect, useRef, useState } from 'react';
import { ArrowDownIcon, Image, Send, Trash2, Users, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { notificationApi } from '../../../config/notification-api';
import CountrySelector from '../../Common/CountrySelector/CountrySelector';
import LanguageSelector from '../../Common/LanguageSelector/LanguageSelector';
import { getCountryName, getLanguageName } from '../../../constants/constants';
import './Notification.css';

const emptyFormData = {
  title: '',
  bodyText: '',
  csvFile: null,
  userData: [],
  image: null,
  targetType: 'csv',
  country: '',
  language: '',
};

const CreateNotificationQueModal = ({
  isOpen,
  onClose,
  onSuccess,
  presetUserCount = null,
  initialTitle = '',
  initialBodyText = '',
  onSubmit,
}) => {
  const isPresetUsersMode = typeof presetUserCount === 'number';

  const [formData, setFormData] = useState(emptyFormData);
  const [showCountryDialog, setShowCountryDialog] = useState(false);
  const [showLanguageDialog, setShowLanguageDialog] = useState(false);
  const [csvError, setCsvError] = useState('');
  const [csvSuccess, setCsvSuccess] = useState('');
  const [sendingQue, setSendingQue] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    setFormData({
      ...emptyFormData,
      title: initialTitle || '',
      bodyText: initialBodyText || '',
      targetType: isPresetUsersMode ? 'preset' : 'csv',
    });
    setCsvError('');
    setCsvSuccess('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [isOpen, initialTitle, initialBodyText, isPresetUsersMode]);

  const handleClose = () => {
    if (sendingQue) return;
    onClose?.();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTargetTypeChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      targetType: e.target.value,
      csvFile: null,
      userData: [],
      country: '',
      language: '',
    }));
    setCsvError('');
    setCsvSuccess('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validatePhoneNumber = (phone) => {
    const newPhone = phone.replace(/\D/g, '');
    return /^\d{8}$/.test(newPhone);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setCsvError('');
    setCsvSuccess('');

    if (!file) {
      setFormData((prev) => ({ ...prev, csvFile: null, userData: [] }));
      return;
    }

    if (!file.name.endsWith('.csv')) {
      setCsvError('Please upload a CSV file only');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter((line) => line.trim());

      if (lines.length === 0) {
        setCsvError('CSV file is empty');
        e.target.value = '';
        return;
      }

      const firstLine = lines[0];
      let delimiter = ',';
      if (firstLine.includes('\t')) {
        delimiter = '\t';
      } else if (firstLine.includes(';')) {
        delimiter = ';';
      } else if (!firstLine.includes(',') && /\s{2,}/.test(firstLine)) {
        delimiter = /\s+/;
      }

      const header = typeof delimiter === 'string'
        ? lines[0].toLowerCase().split(delimiter).map((h) => h.trim())
        : lines[0].toLowerCase().split(delimiter).map((h) => h.trim());

      const hasPhoneColumn = header.some((h) =>
        h === 'phone number' || h === 'phonenumber' || h === 'phone'
      );

      if (!hasPhoneColumn) {
        setCsvError(`CSV must contain "phone number" column (found columns: ${header.join(', ')})`);
        e.target.value = '';
        return;
      }

      const phoneColumnIndex = header.findIndex((h) =>
        h === 'phone number' || h === 'phonenumber' || h === 'phone'
      );
      const nameColumnIndex = header.findIndex((h) =>
        h === 'name' || h === 'full name' || h === 'fullname'
      );

      const userCount = lines.length - 1;
      if (userCount === 0) {
        setCsvError('CSV file contains no user data');
        e.target.value = '';
        return;
      }

      if (userCount > 15000) {
        setCsvError(`CSV contains ${userCount} users. Maximum 15000 users allowed`);
        e.target.value = '';
        return;
      }

      const invalidEntries = [];
      let validPhoneCount = 0;
      const userData = [];

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        const columns = typeof delimiter === 'string'
          ? line.split(delimiter).map((col) => col.trim())
          : line.split(delimiter).map((col) => col.trim());

        const phone = columns[phoneColumnIndex] || '';
        const name = nameColumnIndex !== -1 ? (columns[nameColumnIndex] || '') : '';

        if (!phone) {
          invalidEntries.push({
            lineNumber: i + 1,
            phone: '(empty)',
            reason: 'Phone number is empty',
          });
        } else if (validatePhoneNumber(phone)) {
          validPhoneCount += 1;
          userData.push({
            phone: phone.replace(/\D/g, ''),
            name: name || '',
          });
        } else {
          const newPhone = phone.replace(/\D/g, '');
          const reason = newPhone.length === 0
            ? 'Phone number contains no digits'
            : newPhone.length !== 8
              ? `Phone number must be exactly 8 digits (found ${newPhone.length})`
              : 'Phone number contains invalid characters';

          invalidEntries.push({
            lineNumber: i + 1,
            phone,
            reason,
          });
        }
      }

      if (invalidEntries.length > 0) {
        const errorLines = invalidEntries.slice(0, 5).map((entry) =>
          `Line ${entry.lineNumber}: ${entry.phone} (${entry.reason})`
        ).join('\n');
        const moreErrors = invalidEntries.length > 5
          ? `\n... and ${invalidEntries.length - 5} more errors`
          : '';
        setCsvError(`Invalid phone numbers found:\n${errorLines}${moreErrors}`);
        e.target.value = '';
        return;
      }

      if (validPhoneCount === 0) {
        setCsvError('No valid phone numbers found in CSV');
        e.target.value = '';
        return;
      }

      const nameInfo = nameColumnIndex !== -1 ? ' with names' : '';
      setCsvSuccess(`Successfully loaded ${validPhoneCount} valid user${validPhoneCount !== 1 ? 's' : ''}${nameInfo}`);

      setFormData((prev) => ({
        ...prev,
        csvFile: file,
        userData,
      }));
    };

    reader.onerror = () => {
      setCsvError('Error reading CSV file');
      e.target.value = '';
    };

    reader.readAsText(file);
  };

  const removeFile = () => {
    setFormData((prev) => ({ ...prev, csvFile: null, userData: [] }));
    setCsvError('');
    setCsvSuccess('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 600 * 1024) {
      toast.error('Image size must be less than 600KB');
      return;
    }

    setFormData((prev) => ({ ...prev, image: file }));
  };

  const removeImage = () => {
    setFormData((prev) => ({ ...prev, image: null }));
  };

  const handleCountrySelect = (country) => {
    setFormData((prev) => ({
      ...prev,
      country: country.id || country.name,
      language: '',
    }));
    setShowCountryDialog(false);
  };

  const handleLanguageSelect = (language) => {
    setFormData((prev) => ({
      ...prev,
      language: language.id || language.name,
    }));
    setShowLanguageDialog(false);
  };

  const defaultSubmit = async () => {
    if (formData.targetType === 'csv' && !formData.csvFile) {
      toast.error('Please add a csv file');
      return { success: false };
    }

    if (formData.targetType === 'nationality' && !formData.country) {
      toast.error('Please select a country');
      return { success: false };
    }

    return notificationApi.createQue({
      title: formData.title,
      bodyText: formData.bodyText,
      userData: formData.targetType === 'csv' ? formData.userData : [],
      image: formData.image,
      country: formData.targetType === 'nationality' ? formData.country : null,
      language: formData.targetType === 'nationality' ? formData.language : null,
      targetType: formData.targetType,
    });
  };

  const handleSend = async () => {
    if (!formData.title.trim() || !formData.bodyText.trim()) {
      toast.error('Title and body text are required');
      return;
    }

    setSendingQue(true);

    try {
      const submitPayload = {
        title: formData.title.trim(),
        bodyText: formData.bodyText.trim(),
        image: formData.image,
      };

      const useCustomSubmit = onSubmit && (!isPresetUsersMode || formData.targetType === 'preset');

      const response = useCustomSubmit
        ? await onSubmit(submitPayload)
        : await defaultSubmit();

      if (response?.success) {
        toast.success(response.msg || 'Notification que created successfully!');
        onClose?.();
        onSuccess?.(response);
      } else {
        toast.error(response?.error || response?.msg || 'Failed to create notification que');
      }
    } catch (error) {
      console.error('Error creating notification que:', error);
      toast.error('Error creating notification que. Please try again.');
    } finally {
      setSendingQue(false);
    }
  };

  const isSubmitDisabled = sendingQue
    || !formData.title.trim()
    || !formData.bodyText.trim()
    || (formData.targetType === 'csv' && !formData.csvFile)
    || (formData.targetType === 'nationality' && !formData.country)
    || (formData.targetType === 'preset' && !presetUserCount);

  if (!isOpen) return null;

  return (
    <>
      <div className="notification-modal-overlay">
        <div className="notification-modal-content">
          <div className="notification-modal-header">
            <div>
              <h2 className="notification-modal-title">Create Notification Que</h2>
            </div>
            <button type="button" onClick={handleClose} className="notification-close-btn">
              <X size={24} />
            </button>
          </div>

          <div className="notification-form">
            <div className="notification-form-group">
              <label>Target Type</label>
              <div className="notification-radio-group">
                {isPresetUsersMode && (
                  <label className="notification-radio-label">
                    <input
                      type="radio"
                      name="targetType"
                      value="preset"
                      checked={formData.targetType === 'preset'}
                      onChange={handleTargetTypeChange}
                    />
                    <span>Selected Users</span>
                  </label>
                )}
                <label className="notification-radio-label">
                  <input
                    type="radio"
                    name="targetType"
                    value="csv"
                    checked={formData.targetType === 'csv'}
                    onChange={handleTargetTypeChange}
                  />
                  <span>CSV File</span>
                </label>
                <label className="notification-radio-label">
                  <input
                    type="radio"
                    name="targetType"
                    value="nationality"
                    checked={formData.targetType === 'nationality'}
                    onChange={handleTargetTypeChange}
                  />
                  <span>Nationality</span>
                </label>
              </div>
            </div>

            {formData.targetType === 'preset' && (
              <div className="notification-form-group">
                <label>Users</label>
                <div className="notification-preset-users-box">
                  <Users size={18} />
                  <span>
                    <strong>{presetUserCount}</strong> user{presetUserCount !== 1 ? 's' : ''} selected
                  </span>
                </div>
              </div>
            )}

            {formData.targetType === 'csv' && (
              <div className="notification-form-group">
                <label htmlFor="csvFile">Upload CSV File</label>
                <div className="notification-file-upload-section">
                  <div className="notification-upload-notes">
                    <p className="notification-note-text">CSV must contain column name: &quot;phone number&quot;</p>
                    <p className="notification-note-text">Optional column: &quot;name&quot;</p>
                    <p className="notification-note-text">Phone numbers must be exactly 8 digits</p>
                    <p className="notification-note-text">Don&apos;t include country code or spaces</p>
                  </div>

                  <div className="notification-file-upload-row">
                    <input
                      type="file"
                      id="csvFile"
                      name="csvFile"
                      accept=".csv"
                      onChange={handleFileChange}
                      ref={fileInputRef}
                      required
                    />

                    {formData.csvFile && (
                      <button type="button" onClick={removeFile} className="notification-remove-file-btn">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>

                  {csvError && (
                    <div className="notification-error-message">
                      {csvError.split('\n').map((line, index) => (
                        <div key={index}>{line}</div>
                      ))}
                    </div>
                  )}

                  {csvSuccess && (
                    <div className="notification-success-message">{csvSuccess}</div>
                  )}
                </div>
              </div>
            )}

            {formData.targetType === 'nationality' && (
              <div className="notification-form-group">
                <label>Select Nationality</label>
                <div
                  className="notification-input notification-select-trigger"
                  onClick={() => setShowCountryDialog(true)}
                >
                  <span>{formData.country ? getCountryName(formData.country) : 'Select Country'}</span>
                  <ArrowDownIcon size={16} />
                </div>

                {formData.country === 'india' && (
                  <div className="notification-sub-selection">
                    <label>Select Language (Optional)</label>
                    <div
                      className="notification-input notification-select-trigger"
                      onClick={() => setShowLanguageDialog(true)}
                    >
                      <span>{formData.language ? getLanguageName(formData.language) : 'Select Language'}</span>
                      <ArrowDownIcon size={16} />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="notification-form-group">
              <label>Title</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter title..."
                className="notification-input"
              />
            </div>

            <div className="notification-form-group">
              <label>Image</label>
              <div className="notification-image-upload-section">
                {!formData.image ? (
                  <div className="notification-image-upload-area">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="notification-image-input"
                      id="notificationQueImage"
                    />
                    <label htmlFor="notificationQueImage" className="notification-image-upload-label">
                      <Image size={24} />
                      <span>Click to upload image</span>
                    </label>
                  </div>
                ) : (
                  <div className="notification-image-preview">
                    <img
                      src={URL.createObjectURL(formData.image)}
                      alt="Notification preview"
                      className="notification-preview-image"
                    />
                    <button type="button" onClick={removeImage} className="notification-remove-image-btn">
                      <Trash2 size={14} />
                      Remove
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="notification-form-group">
              <label>Body Text</label>
              <textarea
                name="bodyText"
                value={formData.bodyText}
                onChange={handleInputChange}
                placeholder="Enter text..."
                className="notification-textarea"
                rows="6"
                required
              />
            </div>
          </div>

          <button
            type="button"
            onClick={handleSend}
            disabled={isSubmitDisabled}
            className={`notification-send-btn ${isSubmitDisabled ? 'notification-disabled' : ''}`}
          >
            <Send size={18} />
            {sendingQue ? 'Creating...' : 'Create Notification'}
          </button>
        </div>
      </div>

      <CountrySelector
        isOpen={showCountryDialog}
        onClose={() => setShowCountryDialog(false)}
        onSelect={handleCountrySelect}
        selectedCountryId={formData.country}
      />

      <LanguageSelector
        isOpen={showLanguageDialog}
        onClose={() => setShowLanguageDialog(false)}
        onSelect={handleLanguageSelect}
        selectedLanguageId={formData.language}
      />
    </>
  );
};

export default CreateNotificationQueModal;
